import { sortByName, sortAsc } from '../utils/sortOptions';
import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  BanknotesIcon, ExclamationTriangleIcon, CheckCircleIcon,
  XCircleIcon, PlusIcon, PencilIcon, XMarkIcon,
  ChevronRightIcon, PrinterIcon, EyeIcon, TrashIcon,
} from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .fee * { box-sizing:border-box; }
  .fee { font-family:'Sora',sans-serif; color:#1a1d2e; animation:feeFade .4s ease both; }
  @keyframes feeFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .fee-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .fee-title { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .fee-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }

  .stat-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:.9rem; margin-bottom:1.4rem; }
  .stat-card { background:white; border-radius:14px; padding:1rem 1.1rem; border:1px solid #e8ebf4; position:relative; overflow:hidden; }
  .stat-card::after { content:''; position:absolute; top:0; right:0; width:56px; height:56px; border-radius:0 14px 0 56px; opacity:.07; }
  .stat-card.green::after{background:#10c98e} .stat-card.blue::after{background:#3b6ef8}
  .stat-card.red::after{background:#ef4444}   .stat-card.amber::after{background:#f59e0b}
  .s-icon { width:30px; height:30px; border-radius:9px; display:flex; align-items:center; justify-content:center; margin-bottom:.6rem; }
  .s-icon svg { width:14px; height:14px; stroke-width:1.8; }
  .s-icon.green{background:#ecfdf5;color:#10c98e} .s-icon.blue{background:#eef2ff;color:#3b6ef8}
  .s-icon.red{background:#fef2f2;color:#ef4444}   .s-icon.amber{background:#fffbeb;color:#f59e0b}
  .s-label { font-size:.66rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; margin-bottom:2px; }
  .s-value { font-size:1.1rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; line-height:1; }
  .s-sub   { font-size:.67rem; color:#b0b5cc; margin-top:3px; }

  .tab-row { display:flex; gap:4px; background:#f0f2f7; border-radius:11px; padding:4px; margin-bottom:1.4rem; width:fit-content; max-width:100%; flex-wrap:wrap; }
  .tab-btn { padding:7px 18px; border-radius:8px; border:none; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; cursor:pointer; transition:all .15s; background:transparent; color:#9094a8; }
  .tab-btn.active { background:white; color:#0f1123; box-shadow:0 1px 4px rgba(0,0,0,.1); }

  .filter-row { display:flex; gap:10px; margin-bottom:1.2rem; flex-wrap:wrap; align-items:flex-end; }
  .filter-group { display:flex; flex-direction:column; gap:4px; }
  .filter-label { font-size:.67rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .filter-select,.filter-input { font-family:'Sora',sans-serif; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:9px; background:white; color:#1a1d2e; outline:none; min-width:130px; }
  .filter-select:focus,.filter-input:focus { border-color:#3b6ef8; }

  .btn { display:inline-flex; align-items:center; gap:7px; padding:8px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue{background:#3b6ef8;color:white} .btn-blue:hover{background:#2d5ef0;box-shadow:0 4px 14px rgba(59,110,248,.35)}
  .btn-green{background:#10c98e;color:white} .btn-green:hover{background:#0db87e}
  .btn-amber{background:#f59e0b;color:white} .btn-amber:hover{background:#d97706}
  .btn-ghost{background:#f0f2f7;color:#6b7194} .btn-ghost:hover{background:#e4e7f0}
  .btn-sm{padding:5px 10px;font-size:.72rem} .btn:disabled{opacity:.5;cursor:not-allowed}

  .section-group { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
  .section-group-head { display:flex; align-items:center; justify-content:space-between; padding:13px 18px; cursor:pointer; user-select:none; transition:background .13s; flex-wrap:wrap; gap:8px; }
  .section-group-head:hover { background:#fafbff; }
  .sg-left { display:flex; align-items:center; gap:12px; }
  .sg-chevron { width:18px; height:18px; color:#9094a8; transition:transform .2s; flex-shrink:0; }
  .sg-chevron.open { transform:rotate(90deg); }
  .sg-label { font-size:.92rem; font-weight:700; color:#0f1123; }
  .sg-sub   { font-size:.72rem; color:#9094a8; margin-top:2px; }
  .sg-pills { display:flex; gap:6px; flex-wrap:wrap; }
  .sg-pill { font-size:.68rem; font-weight:600; padding:3px 9px; border-radius:99px; }
  .sg-pill.green{background:#ecfdf5;color:#10c98e} .sg-pill.red{background:#fef2f2;color:#ef4444}
  .sg-pill.amber{background:#fff7ed;color:#ea580c} .sg-pill.gray{background:#f3f4f6;color:#6b7280}
  .sg-pill.blue{background:#eef2ff;color:#3b6ef8}  .sg-pill.purple{background:#f3f0ff;color:#8b5cf6}

  .fee-table { width:100%; border-collapse:collapse; }
  .fee-table thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .fee-table th { padding:9px 14px; text-align:center !important; font-size:.63rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .fee-table tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .fee-table tbody tr:last-child { border-bottom:none; }
  .fee-table tbody tr:hover { background:#fafbff; }
  .fee-table td { padding:10px 14px; font-size:.79rem; vertical-align:middle; text-align:center !important; }

  .status-badge { display:inline-flex; align-items:center; gap:4px; font-size:.67rem; font-weight:600; padding:3px 9px; border-radius:99px; white-space:nowrap; }
  .status-badge.paid{background:#ecfdf5;color:#10c98e} .status-badge.unpaid{background:#fef2f2;color:#ef4444}
  .status-badge.overdue{background:#fff7ed;color:#ea580c} .status-badge.waived{background:#f3f4f6;color:#6b7280}
  .status-badge.pending_verification{background:#fdf4ff;color:#9333ea}
  .amount{font-family:'JetBrains Mono',monospace;font-size:.79rem;font-weight:600;color:#1a1d2e}
  .amount.green{color:#10c98e} .amount.red{color:#ef4444}
  .action-btn{width:28px;height:28px;border-radius:7px;border:1px solid #e0e4f0;background:#f8f9fc;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;transition:all .15s;margin-left:4px}
  .action-btn svg{width:13px;height:13px;stroke-width:1.8}
  .action-btn.pay:hover{background:#ecfdf5;border-color:#10c98e;color:#10c98e}
  .action-btn.edit:hover{background:#eef2ff;border-color:#3b6ef8;color:#3b6ef8}
  .action-btn.print:hover{background:#f3f0ff;border-color:#8b5cf6;color:#8b5cf6}
  .action-btn.fine:hover{background:#fff7ed;border-color:#f59e0b;color:#f59e0b}

  .fee-cards { display:none; padding:10px; }
  .fee-card { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .fee-card-top { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:8px; gap:8px; }
  .fee-card-name { font-size:.86rem; font-weight:700; color:#0f1123; }
  .fee-card-meta { font-size:.7rem; color:#9094a8; margin-top:2px; }
  .fee-card-row { display:flex; justify-content:space-between; align-items:center; font-size:.76rem; padding:4px 0; border-bottom:1px solid #f0f2f8; }
  .fee-card-row:last-of-type { border-bottom:none; }
  .fee-card-label { color:#9094a8; font-weight:600; }
  .fee-card-actions { display:flex; gap:5px; margin-top:8px; flex-wrap:wrap; }
  .fee-card-actions .action-btn { margin-left:0; }

  .fine-chip{display:inline-flex;align-items:center;gap:5px;font-size:.7rem;font-weight:600;padding:3px 8px;border-radius:6px;background:#fff7ed;color:#ea580c;margin:2px}
  .fine-chip.library{background:#faf5ff;color:#9333ea}
  .fine-chip.hostel{background:#eef2ff;color:#3b6ef8}
  .fine-del{cursor:pointer;opacity:.6;margin-left:3px}
  .fine-del:hover{opacity:1}
  .fine-cell-total{font-family:'JetBrains Mono',monospace;font-weight:600;font-size:.78rem}
  .fine-cell-items{display:flex;flex-direction:column;align-items:flex-end;gap:2px;margin-top:2px}

  .fee-empty{text-align:center;padding:3rem 2rem;color:#9094a8;font-size:.82rem}
  .fee-loading{display:flex;align-items:center;justify-content:center;height:160px;gap:10px;color:#9094a8}
  .spinner{width:18px;height:18px;border:2px solid #e4e7f0;border-top-color:#10c98e;border-radius:50%;animation:spin .7s linear infinite}
  @keyframes spin{to{transform:rotate(360deg)}}

  .modal-backdrop{position:fixed;inset:0;background:rgba(10,12,28,.55);backdrop-filter:blur(3px);display:flex;align-items:center;justify-content:center;z-index:1000;padding:20px;animation:feeFade .2s ease}
  .modal-box{background:white;border-radius:20px;width:100%;max-width:480px;border:1px solid #e8ebf4;box-shadow:0 24px 64px rgba(0,0,0,.18);animation:modalIn .25s ease;overflow:hidden;display:flex;flex-direction:column;max-height:90vh}
  @keyframes modalIn{from{opacity:0;transform:translateY(16px) scale(.97)}to{opacity:1;transform:translateY(0) scale(1)}}
  .modal-head{display:flex;align-items:center;justify-content:space-between;padding:16px 20px;border-bottom:1px solid #f0f2f8;flex-shrink:0;flex-wrap:wrap;gap:8px}
  .modal-title{font-size:.92rem;font-weight:700;color:#0f1123}
  .modal-close{width:28px;height:28px;border-radius:8px;border:1px solid #e8ebf4;background:#f8f9fc;display:flex;align-items:center;justify-content:center;cursor:pointer;color:#9094a8;flex-shrink:0}
  .modal-close svg{width:14px;height:14px;stroke-width:2}
  .modal-body{padding:18px 20px;overflow-y:auto;flex:1}
  .modal-footer{display:flex;justify-content:flex-end;gap:8px;padding:14px 20px;border-top:1px solid #f0f2f8;flex-shrink:0;flex-wrap:wrap}
  .form-field{display:flex;flex-direction:column;gap:5px;margin-bottom:13px}
  .form-label{font-size:.68rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.06em}
  .form-input,.form-select{font-family:'Sora',sans-serif;font-size:.82rem;padding:9px 12px;border:1.5px solid #e0e4f0;border-radius:10px;background:#f8f9fc;color:#1a1d2e;outline:none;transition:border-color .15s;width:100%}
  .form-input:focus,.form-select:focus{border-color:#3b6ef8;background:white}
  .form-input.mono{font-family:'JetBrains Mono',monospace}
  .form-row{display:grid;grid-template-columns:1fr 1fr;gap:12px}
  .form-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}
  .form-grid > *, .fg > * { min-width:0; }
  .class-list{max-height:200px;overflow-y:auto;border:1.5px solid #e0e4f0;border-radius:10px}
  .class-list-item{display:flex;align-items:center;justify-content:space-between;padding:9px 12px;border-bottom:1px solid #f3f5fb;font-size:.8rem}
  .class-list-item:last-child{border-bottom:none}
  .fee-tag{font-family:'JetBrains Mono',monospace;font-size:.72rem;font-weight:600;color:#3b6ef8;background:#eef2ff;padding:2px 8px;border-radius:6px}
  .no-fee-tag{font-size:.7rem;color:#ef4444}
  .proof-img{max-width:100%;border-radius:10px;border:1px solid #e8ebf4;margin-top:8px}

  @media (max-width:900px) { .stat-grid { grid-template-columns:repeat(2,1fr); } }
  @media (max-width:768px) {
    .fee-title { font-size:1.1rem; }
    .fee-header { margin-bottom:1rem; }
    .tab-btn { padding:6px 12px; font-size:.74rem; }
    .filter-row { flex-direction:column; gap:8px; }
    .filter-group { width:100%; }
    .filter-select,.filter-input { width:100%; min-width:unset; }
    .fee-table-wrap { display:none !important; }
    .fee-cards { display:block; }
    .modal-body { padding:14px 16px; }
    .modal-footer { justify-content:stretch; }
    .modal-footer .btn { flex:1; justify-content:center; }
    .form-row { grid-template-columns:1fr; }
    .form-grid { grid-template-columns:1fr; }
  }
  @media (max-width:480px) {
    .stat-grid { grid-template-columns:1fr 1fr; }
    .s-value { font-size:.9rem; }
  }
`;

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt     = (n) => `Rs. ${(Number(n)||0).toLocaleString('en-PK')}`;
const fmtHostel = (n) => `Rs. ${(Number(n)||0).toLocaleString('en-PK',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-PK',{day:'numeric',month:'short',year:'numeric'}) : '—';
const monthLabel = (m) => { if(!m) return '—'; const [y,mn]=String(m).split('-').map(Number); return new Date(y,mn-1,1).toLocaleDateString('en-US',{month:'short',year:'numeric'}); };
const curMonth = () => new Date().toISOString().slice(0,7);
const sumFines = (fines) => (fines||[]).reduce((s,f) => s + Number(f.amount||0), 0);
// Library/hostel fines are pushed here automatically from those panels (tagged
// module:'library'/'hostel' at the source — see libraryController.writeLibraryFine /
// hostelOpsController.writeHostelFine); anything else (manually added on this page,
// or older fines from before this tagging existed) falls under "Other".
const finesByModule = (fines, mod) => (fines||[]).filter(f => f.module === mod);
const otherFines     = (fines) => (fines||[]).filter(f => f.module !== 'library' && f.module !== 'hostel');
const sumOf           = (list) => list.reduce((s,f) => s + Number(f.amount||0), 0);
const fineLabel       = (f) => f.module === 'library' ? 'Library Fine' : f.module === 'hostel' ? 'Hostel Fine' : 'Fine';

// One of the three fine-source columns (Library / Hostel / Other) — shows the
// column's total plus each itemized fine underneath, or a dash when there's none.
const FineCell = ({ list, chipClass }) => {
  if (!list.length) return <span style={{color:'#c0c5dc'}}>—</span>;
  return (
    <div>
      <span className="fine-cell-total" style={{color:'#ea580c'}}>+{fmt(sumOf(list))}</span>
      <div className="fine-cell-items">
        {list.map(f => (
          <span key={f.id||f._id} className={`fine-chip ${chipClass}`}>{f.reason}: {fmt(f.amount)}</span>
        ))}
      </div>
    </div>
  );
};

const STATUS_LABELS = {
  paid:'Paid', unpaid:'Unpaid', overdue:'Overdue',
  waived:'Waived', pending_verification:'Proof Submitted',
};

const FINE_PRESETS = [
  { reason:'Late Fee',       amount:200  },
  { reason:'Uniform Fine',   amount:300  },
  { reason:'Library Fine',   amount:100  },
  { reason:'Exam Fee',       amount:500  },
  { reason:'Damage / Loss',  amount:1000 },
  { reason:'Transport Fine', amount:200  },
  { reason:'Other',          amount:0    },
];

const printChallan = (rec, school) => {
  const win = window.open('','_blank','width=800,height=600');
  win.document.write(`<!DOCTYPE html><html><head><title>Fee Challan</title>
  <style>* { box-sizing:border-box; margin:0; padding:0; } body { font-family:Arial,sans-serif; color:#111; } .challan { width:700px; margin:30px auto; border:2px solid #333; border-radius:4px; } .header { background:#1a1d2e; color:white; padding:16px 20px; display:flex; align-items:center; justify-content:space-between; } .school-name { font-size:18px; font-weight:700; } .challan-title { font-size:13px; font-weight:700; background:rgba(255,255,255,.15); padding:6px 14px; border-radius:4px; } .body { padding:16px 20px; } .info-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-bottom:14px; } .info-row { display:flex; flex-direction:column; gap:2px; padding:8px 10px; background:#f8f9fc; border-radius:4px; } .info-key { font-size:9px; font-weight:700; color:#888; text-transform:uppercase; } .info-val { font-size:13px; font-weight:600; } table { width:100%; border-collapse:collapse; margin-bottom:14px; } th { padding:8px 10px; text-align:left; font-size:10px; font-weight:700; color:#888; background:#f8f9fc; border-bottom:1px solid #eee; } td { padding:8px 10px; font-size:12px; border-bottom:1px solid #f5f5f5; } .total-row td { font-weight:700; background:#f0f9ff; border-top:2px solid #3b6ef8; } .footer { padding:10px 20px; background:#f8f9fc; font-size:10px; color:#888; text-align:center; border-top:1px solid #eee; } @media print { body{margin:0} }</style>
  </head><body>
  <div class="challan">
    <div class="header"><div><div class="school-name">${school?.name||'School Portal'}</div></div><div class="challan-title">FEE CHALLAN</div></div>
    <div class="body">
      <div class="info-grid">
        <div class="info-row"><span class="info-key">Student</span><span class="info-val">${rec.student?.user?.name||'—'}</span></div>
        <div class="info-row"><span class="info-key">Admission No</span><span class="info-val">${rec.student?.admissionNo||'—'}</span></div>
        <div class="info-row"><span class="info-key">Class / Section</span><span class="info-val">${rec.class?.name||'—'} — ${rec.section||'—'}</span></div>
        <div class="info-row"><span class="info-key">Month</span><span class="info-val">${monthLabel(rec.month)}</span></div>
        <div class="info-row"><span class="info-key">Due Date</span><span class="info-val">${rec.dueDate?new Date(rec.dueDate).toLocaleDateString('en-PK',{day:'numeric',month:'long',year:'numeric'}):'—'}</span></div>
        <div class="info-row"><span class="info-key">Status</span><span class="info-val">${STATUS_LABELS[rec.status]||rec.status}</span></div>
      </div>
      <table><thead><tr><th>Description</th><th style="text-align:right">Amount</th></tr></thead>
      <tbody>
        <tr><td>Tuition Fee — ${monthLabel(rec.month)}</td><td style="text-align:right;font-family:monospace">Rs. ${(Number(rec.amount)||0).toLocaleString('en-PK')}</td></tr>
        <tr><td>Hostel Fee</td><td style="text-align:right;font-family:monospace">${fmtHostel(rec.hostelFee)}</td></tr>
        ${(rec.fines||[]).map(f=>`<tr><td style="color:#ea580c">${fineLabel(f)}: ${f.reason}</td><td style="text-align:right;font-family:monospace;color:#ea580c">+ Rs. ${(Number(f.amount)||0).toLocaleString('en-PK')}</td></tr>`).join('')}
        ${Number(rec.discount)>0?`<tr><td style="color:#10c98e">Discount</td><td style="text-align:right;font-family:monospace;color:#10c98e">− Rs. ${(Number(rec.discount)||0).toLocaleString('en-PK')}</td></tr>`:''}
      </tbody>
      <tfoot><tr class="total-row"><td>Total Due</td><td style="text-align:right;font-family:monospace">Rs. ${(Number(rec.totalDue)||0).toLocaleString('en-PK')}</td></tr></tfoot></table>
    </div>
    <div class="footer">${school?.name||'School Portal'} · Generated ${new Date().toLocaleDateString('en-PK')}</div>
  </div>
  <script>window.onload=()=>{window.print();}<\/script></body></html>`);
  win.document.close();
};

// ── Component ─────────────────────────────────────────────────────────────────
const Fees = () => {
  const [tab, setTab]             = useState('records');
  const [classes, setClasses]     = useState([]);
  const [fineReasons, setFineReasons] = useState([]);
  const [month, setMonth]         = useState(curMonth());
  const [filterClass, setFilterClass]     = useState('');
  const [filterSection, setFilterSection] = useState('');
  const [filterStatus, setFilterStatus]   = useState('');
  const [grouped, setGrouped]     = useState([]);
  const [defaulters, setDefaulters] = useState([]);
  const [proofs, setProofs]       = useState([]);
  const [stats, setStats]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [expanded, setExpanded]   = useState({});
  const [search, setSearch]       = useState('');

  const [genModal, setGenModal]     = useState(false);
  const [genMonth, setGenMonth]     = useState(curMonth());
  const [genDueDay, setGenDueDay]   = useState('10');
  const [genClassId, setGenClassId] = useState('');
  const [generating, setGenerating] = useState(false);

  const [payModal, setPayModal] = useState(null);
  const [payForm, setPayForm]   = useState({paidAmount:'',paymentMethod:'cash',receiptNo:'',remarks:''});
  const [paying, setPaying]     = useState(false);

  const [editModal, setEditModal] = useState(null);
  // No discount field here on purpose — that's the dedicated Discount button/modal's
  // job (openDiscModal/handleAddDiscount below); having it in two places risked one
  // overwriting the other with whatever this modal happened to be holding.
  const [editForm, setEditForm]   = useState({status:'unpaid',remarks:''});
  const [editing, setEditing]     = useState(false);

  const [discModal, setDiscModal] = useState(null);
  const [discForm, setDiscForm]   = useState({discount:0,discountReason:''});
  const [addingDisc, setAddingDisc] = useState(false);

  const [fineModal, setFineModal] = useState(null);
  const [fineForm, setFineForm]   = useState({reason:'Late Fee',customReason:'',description:'',amount:'200'});
  const [addingFine, setAddingFine] = useState(false);

  const [challanRec, setChallanRec]       = useState(null);
  const [challanSchool, setChallanSchool] = useState(null);
  const [proofView, setProofView]         = useState(null);
  const [regenModal, setRegenModal]       = useState(null);
  const [regenForm,  setRegenForm]        = useState({ visibleDay:'1', dueDay:'10', amount:'', note:'' });
  const [regening,   setRegening]         = useState(false);

  useEffect(() => {
    // FIX: backend returns id (MySQL), mapped to _id in controller — use id as fallback
    api.get('/admin/classes').then(r=>setClasses(r.data||[])).catch(()=>{});
    api.get('/admin/fees/fine-reasons').then(r=>setFineReasons(r.data||[])).catch(()=>{});
  }, []);

  // FIX: classes use numeric id from MySQL — compare as String to handle both
  const getSections = () => {
    const cls = classes.find(c => String(c.id||c._id) === String(filterClass));
    return cls?.sections || [];
  };

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({month});
      if (filterClass)   p.append('classId', filterClass);
      if (filterSection) p.append('section', filterSection);
      if (filterStatus)  p.append('status', filterStatus);
      const {data} = await api.get(`/admin/fees?${p}`);
      setGrouped(data.grouped||[]); setStats(data.stats||[]);
      const exp = {};
      (data.grouped||[]).forEach(g => { exp[`${g.classId}__${g.section}`] = true; });
      setExpanded(exp);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [month, filterClass, filterSection, filterStatus]);

  const fetchDefaulters = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({month});
      if (filterClass)   p.append('classId', filterClass);
      if (filterSection) p.append('section', filterSection);
      const {data} = await api.get(`/admin/fees/defaulters?${p}`);
      setDefaulters(data||[]);
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  }, [month, filterClass, filterSection]);

  const fetchProofs = useCallback(async () => {
    setLoading(true);
    try { const {data} = await api.get('/admin/fees/proofs'); setProofs(data||[]); }
    catch { toast.error('Failed'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (tab==='records')    fetchRecords();
    if (tab==='defaulters') fetchDefaulters();
    if (tab==='proofs')     fetchProofs();
    // Always fetch proofs in background so badge count stays accurate on all tabs
    if (tab!=='proofs') fetchProofs();
  }, [tab, month, filterClass, filterSection, filterStatus]);

  const toggleGroup = (key) => setExpanded(prev => ({...prev, [key]: !prev[key]}));

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const body = {month: genMonth, dueDay: Number(genDueDay)};
      if (genClassId) body.classId = genClassId;
      const {data} = await api.post('/admin/fees/generate', body);
      toast.success(data.message); setGenModal(false); fetchRecords();
    } catch(err) { toast.error(err.response?.data?.message||'Failed'); }
    finally { setGenerating(false); }
  };

  // FIX: use rec.id (MySQL primary key); backend also maps to rec._id for safety
  const recId = (rec) => rec.id || rec._id;

  const openPayModal = (rec) => {
    setPayModal(rec);
    setPayForm({paidAmount: String(Number(rec.totalDue)||0), paymentMethod:'cash', receiptNo:'', remarks:''});
  };
  const handlePay = async () => {
    setPaying(true);
    try {
      await api.put(`/admin/fees/${recId(payModal)}/pay`, payForm);
      toast.success('Marked as paid'); setPayModal(null);
      // Always refresh all three — paying from Proofs tab must update stats cards
      // and remove from proofs list simultaneously
      fetchRecords();
      fetchProofs();
    } catch { toast.error('Failed'); }
    finally { setPaying(false); }
  };

  const openEditModal = (rec) => {
    setEditModal(rec);
    setEditForm({status: rec.status, remarks: rec.remarks||''});
  };
  const handleEdit = async () => {
    setEditing(true);
    try {
      await api.put(`/admin/fees/${recId(editModal)}`, editForm);
      toast.success('Updated'); setEditModal(null); fetchRecords();
    } catch { toast.error('Failed'); }
    finally { setEditing(false); }
  };

  const openDiscModal = (rec) => {
    setDiscModal(rec);
    setDiscForm({discount: Number(rec.discount)||0, discountReason: rec.remarks||''});
  };
  const handleAddDiscount = async () => {
    if (discForm.discount < 0) { toast.error('Discount cannot be negative'); return; }
    if (!discForm.discountReason.trim()) { toast.error('Reason is required'); return; }
    setAddingDisc(true);
    try {
      await api.put(`/admin/fees/${recId(discModal)}`, {discount: discForm.discount, remarks: discForm.discountReason});
      toast.success('Discount applied'); setDiscModal(null); fetchRecords();
    } catch { toast.error('Failed'); }
    finally { setAddingDisc(false); }
  };

  const openFineModal = (rec) => {
    setFineModal(rec);
    setFineForm({reason:'Late Fee', customReason:'', description:'', amount:'200'});
  };
  const handleAddFine = async () => {
    if (fineForm.reason==='Other' && !fineForm.customReason.trim()) { toast.error('Reason is required'); return; }
    const reason = fineForm.reason==='Other' ? (fineForm.customReason||'Other') : fineForm.reason;
    if (!fineForm.amount || isNaN(fineForm.amount)) { toast.error('Enter fine amount'); return; }
    setAddingFine(true);
    try {
      await api.post(`/admin/fees/${recId(fineModal)}/fine`, {reason, description: fineForm.description, amount: Number(fineForm.amount)});
      toast.success('Fine added'); setFineModal(null); fetchRecords();
    } catch { toast.error('Failed'); }
    finally { setAddingFine(false); }
  };

  const openChallan = async (rec) => {
    try {
      const {data} = await api.get(`/admin/fees/${recId(rec)}`);
      setChallanRec(data.record); setChallanSchool(data.school);
    } catch { toast.error('Failed to load challan'); }
  };

  const summary = {
    paid:        stats.find(x => x._id==='paid')?.count || 0,
    unpaid:      stats.find(x => x._id==='unpaid')?.count || 0,
    overdue:     stats.find(x => x._id==='overdue')?.count || 0,
    collected:   Number(stats.find(x => x._id==='paid')?.paidAmount) || 0,
    outstanding: (Number(stats.find(x => x._id==='unpaid')?.totalAmount) || 0) +
                 (Number(stats.find(x => x._id==='overdue')?.totalAmount) || 0),
    // proofs count comes from proofs array (status stays unpaid/overdue after proof submission)
    proofCount:  proofs.length,
  };

  const filterBySearch = (records) => {
    if (!search.trim()) return records;
    const q = search.trim().toLowerCase();
    return records.filter(r =>
      r.student?.user?.name?.toLowerCase().includes(q) ||
      r.student?.admissionNo?.toLowerCase().includes(q)
    );
  };

  const openRegenModal = (rec) => {
    setRegenModal(rec);
    setRegenForm({ visibleDay:'1', dueDay:'10', amount: String(Number(rec.amount)||''), note:'' });
  };
  const handleRegen = async () => {
    if (!regenForm.dueDay || !regenForm.visibleDay) return toast.error('Days required');
    setRegening(true);
    try {
      await api.post(`/admin/fees/${recId(regenModal)}/regenerate`, {
        visibleDay: Number(regenForm.visibleDay),
        dueTillDay: Number(regenForm.dueDay),
        ...(regenForm.amount ? { amount: Number(regenForm.amount) } : {}),
      });
      toast.success('Bill regenerated successfully');
      setRegenModal(null);
      fetchRecords(); fetchProofs();
    } catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setRegening(false); }
  };

  const RecordActions = ({ rec }) => (
    <>
      {rec.status!=='paid' && rec.status!=='waived' && (
        <button className="action-btn pay" onClick={()=>openPayModal(rec)} title="Mark Paid" style={{color:'#10c98e'}}><CheckCircleIcon /></button>
      )}
      <button className="action-btn fine" onClick={()=>openFineModal(rec)} title="Add Fine" style={{color:'#f59e0b'}}><PlusIcon /></button>
      <button className="action-btn edit" onClick={()=>openDiscModal(rec)} title="Discount" style={{color:'#10c98e',fontSize:'.7rem',fontWeight:700,fontFamily:'Sora'}}>%</button>
      <button className="action-btn edit" onClick={()=>openEditModal(rec)} title="Edit" style={{color:'#3b6ef8'}}><PencilIcon /></button>
      {rec.status === 'waived' && (
        <button className="action-btn" onClick={()=>openRegenModal(rec)} title="Regenerate Bill"
          style={{color:'#0d9488',fontSize:'.72rem',fontWeight:700,fontFamily:'Sora',minWidth:24,justifyContent:'center'}}>↺</button>
      )}
      <button className="action-btn print" onClick={()=>openChallan(rec)} title="Challan" style={{color:'#8b5cf6'}}><PrinterIcon /></button>
    </>
  );

  const renderTable = (records) => {
    const filtered = filterBySearch(records);
    if (filtered.length===0 && search.trim()) return (
      <div style={{padding:'2rem',textAlign:'center',color:'#9094a8',fontSize:'.82rem'}}>No students matching "<strong>{search}</strong>"</div>
    );
    return (
      <>
        {/* Desktop table */}
        <div className="fee-table-wrap" style={{overflowX:'auto'}}>
          <table className="fee-table">
            <thead><tr>
              <th style={{width:32}}>#</th>
              <th>Student</th>
              <th style={{textAlign:'center'}}>Adm No</th>
              <th style={{textAlign:'center'}}>Month</th>
              <th style={{textAlign:'right'}}>Base Fee</th>
              <th style={{textAlign:'right'}}>Hostel Fee</th>
              <th style={{textAlign:'right'}}>Library Fine</th>
              <th style={{textAlign:'right'}}>Hostel Fine</th>
              <th style={{textAlign:'right'}}>Other Fines</th>
              <th style={{textAlign:'right'}}>Discount</th>
              <th style={{textAlign:'right'}}>Total Due</th>
              <th style={{textAlign:'center'}}>Due Date</th>
              <th style={{textAlign:'center'}}>Status</th>
              <th style={{textAlign:'center'}}>Paid Info</th>
              <th style={{textAlign:'center'}}>Actions</th>
            </tr></thead>
            <tbody>
              {filtered.map((rec, i) => {
                const libFines  = finesByModule(rec.fines, 'library');
                const hosFines  = finesByModule(rec.fines, 'hostel');
                const othFines  = otherFines(rec.fines);
                return (
                  <tr key={recId(rec)}>
                    <td style={{color:'#9094a8',fontFamily:"'JetBrains Mono',monospace",fontSize:'.72rem',textAlign:'center'}}>{i+1}</td>
                    <td>
                      <div style={{fontWeight:600,fontSize:'.82rem'}}>{rec.student?.user?.name}</div>
                      <div style={{fontSize:'.68rem',color:'#9094a8'}}>{rec.student?.user?.email}</div>
                      {rec.paymentProofUrl && (
                        <button className="btn btn-ghost btn-sm" style={{fontSize:'.66rem',padding:'2px 7px',color:'#9333ea',background:'#fdf4ff',border:'1px solid #e9d5ff',marginTop:4}} onClick={()=>setProofView(rec)}>📎 View Proof</button>
                      )}
                    </td>
                    <td style={{textAlign:'center'}}><span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.74rem',color:'#6b7194'}}>{rec.student?.admissionNo}</span></td>
                    <td style={{textAlign:'center'}}><span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.76rem',fontWeight:600}}>{monthLabel(rec.month)}</span></td>
                    <td style={{textAlign:'right'}}><span className="amount">{fmt(rec.amount)}</span></td>
                    <td style={{textAlign:'right'}}><span className="amount">{fmtHostel(rec.hostelFee)}</span></td>
                    <td style={{textAlign:'right'}}><FineCell list={libFines} chipClass="library" /></td>
                    <td style={{textAlign:'right'}}><FineCell list={hosFines} chipClass="hostel" /></td>
                    <td style={{textAlign:'right'}}><FineCell list={othFines} chipClass="" /></td>
                    <td style={{textAlign:'right'}}>{Number(rec.discount)>0 ? <span className="amount green">-{fmt(rec.discount)}</span> : <span style={{color:'#c0c5dc'}}>—</span>}</td>
                    <td style={{textAlign:'right'}}><span className="amount">{fmt(rec.totalDue)}</span></td>
                    <td style={{textAlign:'center',fontSize:'.74rem',color:'#6b7194'}}>{fmtDate(rec.dueDate)}</td>
                    <td style={{textAlign:'center'}}><span className={`status-badge ${rec.status}`}>{STATUS_LABELS[rec.status]}</span></td>
                    <td style={{textAlign:'center',fontSize:'.72rem',color:'#6b7194'}}>
                      {rec.status==='paid' ? (
                        <div>
                          <div>{fmtDate(rec.paidAt)}</div>
                          <div style={{color:'#9094a8',fontSize:'.66rem'}}>{rec.paidBy}{rec.receiptNo ? ` · #${rec.receiptNo}` : ''}</div>
                        </div>
                      ) : <span style={{color:'#c0c5dc'}}>—</span>}
                    </td>
                    <td style={{textAlign:'center',whiteSpace:'nowrap'}}><RecordActions rec={rec} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="fee-cards">
          {filtered.map((rec) => {
            const libFines = finesByModule(rec.fines, 'library');
            const hosFines = finesByModule(rec.fines, 'hostel');
            const othFines = otherFines(rec.fines);
            return (
              <div key={recId(rec)} className="fee-card">
                <div className="fee-card-top">
                  <div>
                    <div className="fee-card-name">{rec.student?.user?.name}</div>
                    <div className="fee-card-meta">{rec.student?.admissionNo} · {monthLabel(rec.month)}</div>
                  </div>
                  <span className={`status-badge ${rec.status}`}>{STATUS_LABELS[rec.status]}</span>
                </div>
                <div className="fee-card-row"><span className="fee-card-label">Base Fee</span><span className="amount">{fmt(rec.amount)}</span></div>
                <div className="fee-card-row"><span className="fee-card-label">Hostel Fee</span><span className="amount">{fmtHostel(rec.hostelFee)}</span></div>
                {libFines.length>0 && <div className="fee-card-row"><span className="fee-card-label">Library Fine</span><FineCell list={libFines} chipClass="library" /></div>}
                {hosFines.length>0 && <div className="fee-card-row"><span className="fee-card-label">Hostel Fine</span><FineCell list={hosFines} chipClass="hostel" /></div>}
                {othFines.length>0 && <div className="fee-card-row"><span className="fee-card-label">Other Fines</span><FineCell list={othFines} chipClass="" /></div>}
                {Number(rec.discount)>0 && <div className="fee-card-row"><span className="fee-card-label">Discount</span><span className="amount green">-{fmt(rec.discount)}</span></div>}
                <div className="fee-card-row"><span className="fee-card-label">Total Due</span><span className="amount" style={{fontSize:'.84rem'}}>{fmt(rec.totalDue)}</span></div>
                <div className="fee-card-row"><span className="fee-card-label">Due Date</span><span style={{fontSize:'.74rem',color:'#6b7194'}}>{fmtDate(rec.dueDate)}</span></div>
                {rec.status==='paid' && <div className="fee-card-row"><span className="fee-card-label">Paid On</span><span style={{fontSize:'.74rem',color:'#10c98e'}}>{fmtDate(rec.paidAt)}</span></div>}
                {rec.paymentProofUrl && (
                  <button className="btn btn-ghost btn-sm" style={{fontSize:'.66rem',padding:'2px 7px',color:'#9333ea',background:'#fdf4ff',border:'1px solid #e9d5ff',marginTop:6}} onClick={()=>setProofView(rec)}>📎 View Proof</button>
                )}
                <div className="fee-card-actions"><RecordActions rec={rec} /></div>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div className="fee">
      <style>{style}</style>
      <div className="fee-header">
        <div><div className="fee-title">Fees Management</div><div className="fee-sub">Generate bills · add fines · print challans · track payments</div></div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <button className="btn btn-blue" onClick={()=>setGenModal(true)}><PlusIcon />Generate Bills</button>
        </div>
      </div>

      {/* Stats */}
      <div className="stat-grid">
        <div className="stat-card green"><div className="s-icon green"><CheckCircleIcon /></div><div className="s-label">Collected</div><div className="s-value" style={{fontSize:'1rem'}}>{fmt(summary.collected)}</div><div className="s-sub">{summary.paid} paid</div></div>
        <div className="stat-card red"><div className="s-icon red"><XCircleIcon /></div><div className="s-label">Outstanding</div><div className="s-value" style={{fontSize:'1rem'}}>{fmt(summary.outstanding)}</div><div className="s-sub">{summary.unpaid+summary.overdue} pending</div></div>
        <div className="stat-card amber"><div className="s-icon amber"><ExclamationTriangleIcon /></div><div className="s-label">Overdue</div><div className="s-value">{summary.overdue}</div><div className="s-sub">past due date</div></div>
        <div className="stat-card blue"><div className="s-icon blue"><BanknotesIcon /></div><div className="s-label">Proof Pending</div><div className="s-value">{summary.proofCount}</div><div className="s-sub">waiting verify</div></div>
      </div>

      {/* Tabs — FIX: Proofs tab was missing onClick */}
      <div className="tab-row">
        <button className={`tab-btn ${tab==='records'?'active':''}`}    onClick={()=>setTab('records')}>All Records</button>
        <button className={`tab-btn ${tab==='defaulters'?'active':''}`} onClick={()=>setTab('defaulters')}>Defaulters</button>
        <button className={`tab-btn ${tab==='proofs'?'active':''}`}     onClick={()=>setTab('proofs')}>
          Proofs {summary.proofCount>0 && <span style={{marginLeft:4,background:'#9333ea',color:'white',borderRadius:99,fontSize:'.64rem',padding:'1px 6px'}}>{summary.proofCount}</span>}
        </button>
      </div>

      {/* Filters */}
      <div className="filter-row">
        {tab!=='proofs' && <>
          <div className="filter-group"><label className="filter-label">Month</label><input type="month" className="filter-input" value={month} onChange={e=>setMonth(e.target.value)} /></div>
          <div className="filter-group"><label className="filter-label">Class</label>
            <select className="filter-select" value={filterClass} onChange={e=>{setFilterClass(e.target.value);setFilterSection('');}}>
              <option value="">All Classes</option>
              {/* FIX: use c.id (MySQL) with fallback to c._id */}
              {sortByName(classes).map(c=><option key={c.id||c._id} value={c.id||c._id}>{c.name}</option>)}
            </select>
          </div>
          <div className="filter-group"><label className="filter-label">Section</label>
            <select className="filter-select" value={filterSection} onChange={e=>setFilterSection(e.target.value)} disabled={!filterClass}>
              <option value="">{filterClass?'All Sections':'—'}</option>
              {sortAsc(getSections()).map(s=><option key={s} value={s}>Section {s}</option>)}
            </select>
          </div>
        </>}
        <div className="filter-group"><label className="filter-label">Search</label>
          <input type="text" className="filter-input" value={search} onChange={e=>setSearch(e.target.value)} placeholder="Name or admission no…" />
        </div>
        {tab==='records' && <div className="filter-group"><label className="filter-label">Status</label>
          <select className="filter-select" value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
            <option value="">All</option><option value="overdue">Overdue</option><option value="paid">Paid</option><option value="pending_verification">Proof Submitted</option>
            <option value="unpaid">Unpaid</option><option value="waived">Waived</option>
          </select>
        </div>}
      </div>

      {loading ? (
        <div className="fee-loading"><div className="spinner"/>Loading…</div>
      ) : tab==='records' ? (
        grouped.length===0 ? (
          <div style={{background:'white',borderRadius:16,border:'1px solid #e8ebf4',padding:'3rem 2rem',textAlign:'center',color:'#9094a8',fontSize:'.82rem'}}>
            No fee records for {month}. Click <strong>Generate Bills</strong> to create monthly bills.
          </div>
        ) : grouped.map(group => {
          const key = `${group.classId}__${group.section}`;
          const open = expanded[key] !== false;
          const s = group.stats;
          return (
            <div key={key} className="section-group">
              <div className="section-group-head" onClick={()=>toggleGroup(key)}>
                <div className="sg-left">
                  <ChevronRightIcon className={`sg-chevron ${open?'open':''}`} />
                  <div><div className="sg-label">{group.className} — Section {group.section}</div><div className="sg-sub">{group.records.length} students</div></div>
                </div>
                <div className="sg-pills">
                  {s.paid>0    && <span className="sg-pill green">✓ {s.paid} Paid</span>}
                  {s.unpaid>0  && <span className="sg-pill red">✗ {s.unpaid} Unpaid</span>}
                  {s.overdue>0 && <span className="sg-pill amber">⚠ {s.overdue} Overdue</span>}
                  {s.pending_verification>0 && <span className="sg-pill purple">📎 {s.pending_verification}</span>}
                  {/* FIX: wrap collected in Number() */}
                  <span className="sg-pill blue">{fmt(Number(s.collected)||0)}</span>
                </div>
              </div>
              {open && renderTable(group.records)}
            </div>
          );
        })
      ) : tab==='defaulters' ? (
        defaulters.length===0 ? (
          <div style={{background:'white',borderRadius:16,border:'1px solid #e8ebf4',padding:'3rem 2rem',textAlign:'center',color:'#9094a8',fontSize:'.82rem'}}>🎉 No defaulters for {month}!</div>
        ) : (
          <div className="section-group">
            <div className="section-group-head" style={{cursor:'default'}}>
              <div className="sg-label">Defaulters — {defaulters.length} students</div>
              {/* FIX: Number() wrap on reduce */}
              <span className="sg-pill red">{fmt(defaulters.reduce((s,r)=>s+Number(r.totalDue||0),0))} outstanding</span>
            </div>
            {renderTable(defaulters)}
          </div>
        )
      ) : (
        proofs.length===0 ? (
          <div style={{background:'white',borderRadius:16,border:'1px solid #e8ebf4',padding:'3rem 2rem',textAlign:'center',color:'#9094a8',fontSize:'.82rem'}}>No payment proofs pending verification.</div>
        ) : (
          <div className="section-group">
            <div className="section-group-head" style={{cursor:'default'}}><div className="sg-label">Payment Proofs — {proofs.length} pending</div></div>
            {renderTable(proofs)}
          </div>
        )
      )}

      {/* ── Generate Modal ── */}
      {genModal && createPortal(
        <div className="modal-backdrop" onClick={()=>setGenModal(false)}>
          <div className="modal-box" onClick={e=>e.stopPropagation()}>
            <div className="modal-head"><div className="modal-title">Generate Monthly Fee Bills</div><button className="modal-close" onClick={()=>setGenModal(false)}><XMarkIcon /></button></div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-field"><label className="form-label">Month</label><input type="month" className="form-input" value={genMonth} onChange={e=>setGenMonth(e.target.value)} /></div>
                <div className="form-field"><label className="form-label">Due Day</label><input type="number" min={1} max={28} className="form-input mono" value={genDueDay} onChange={e=>setGenDueDay(e.target.value)} /></div>
              </div>
              <div className="form-field"><label className="form-label">Class (blank = all)</label>
                <select className="form-select" value={genClassId} onChange={e=>setGenClassId(e.target.value)}>
                  <option value="">All Classes</option>
                  {classes.map(c=><option key={c.id||c._id} value={c.id||c._id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-field"><label className="form-label">Fee per class</label>
                <div className="class-list">
                  {classes.filter(c => !genClassId || String(c.id||c._id)===String(genClassId)).map(c=>(
                    <div key={c.id||c._id} className="class-list-item">
                      <span style={{fontWeight:600}}>{c.name}</span>
                      {Number(c.feeAmount||c.fee_amount)>0
                        ? <span className="fee-tag">{fmt(c.feeAmount||c.fee_amount)}/mo</span>
                        : <span className="no-fee-tag">No fee set</span>}
                    </div>
                  ))}
                </div>
              </div>
              <div style={{background:'#fafbff',border:'1px solid #e8ebf4',borderRadius:9,padding:'9px 12px',fontSize:'.74rem',color:'#6b7194'}}>ℹ️ Students already billed are skipped.</div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setGenModal(false)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleGenerate} disabled={generating}><PlusIcon />{generating?'Generating…':'Generate Bills'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── Pay Modal ── */}
      {payModal && createPortal(
        <div className="modal-backdrop" onClick={()=>setPayModal(null)}>
          <div className="modal-box" onClick={e=>e.stopPropagation()}>
            <div className="modal-head"><div className="modal-title">Mark Fee as Paid</div><button className="modal-close" onClick={()=>setPayModal(null)}><XMarkIcon /></button></div>
            <div className="modal-body">
              <div style={{background:'#f8f9fc',border:'1px solid #e8ebf4',borderRadius:10,padding:'12px',marginBottom:14}}>
                <div style={{fontWeight:700,fontSize:'.88rem',color:'#0f1123',marginBottom:6}}>{payModal.student?.user?.name}</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                  <div><div style={{fontSize:'.67rem',color:'#9094a8',textTransform:'uppercase',fontWeight:600}}>Month</div><div style={{fontWeight:600}}>{monthLabel(payModal.month)}</div></div>
                  {/* FIX: Number() on totalDue */}
                  <div><div style={{fontSize:'.67rem',color:'#9094a8',textTransform:'uppercase',fontWeight:600}}>Total Due</div><div style={{fontWeight:700,color:'#ef4444'}}>{fmt(Number(payModal.totalDue)||0)}</div></div>
                </div>
                {payModal.paymentProofUrl && (
                  <div style={{marginTop:8}}>
                    <div style={{fontSize:'.72rem',color:'#9333ea',fontWeight:600,marginBottom:4}}>📎 Student submitted proof:</div>
                    <img src={payModal.paymentProofUrl} alt="proof" style={{maxWidth:'100%',borderRadius:8,border:'1px solid #e9d5ff'}} onError={e=>{e.target.style.display='none';}} />
                  </div>
                )}
              </div>
              <div className="form-row">
                <div className="form-field"><label className="form-label">Amount Received</label><input type="number" className="form-input mono" value={payForm.paidAmount} onChange={e=>setPayForm({...payForm,paidAmount:e.target.value})} /></div>
                <div className="form-field"><label className="form-label">Payment Method</label>
                  <select className="form-select" value={payForm.paymentMethod} onChange={e=>setPayForm({...payForm,paymentMethod:e.target.value})}>
                    <option value="cash">Cash</option><option value="bank">Bank Transfer</option><option value="online">Online</option><option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div className="form-field"><label className="form-label">Receipt No</label><input type="text" className="form-input mono" value={payForm.receiptNo} onChange={e=>setPayForm({...payForm,receiptNo:e.target.value})} placeholder="RCP-001" /></div>
              <div className="form-field"><label className="form-label">Remarks</label><input type="text" className="form-input" value={payForm.remarks} onChange={e=>setPayForm({...payForm,remarks:e.target.value})} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setPayModal(null)}>Cancel</button>
              <button className="btn btn-green" onClick={handlePay} disabled={paying}><CheckCircleIcon />{paying?'Saving…':'Confirm Payment'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── Fine Modal ── */}
      {fineModal && createPortal(
        <div className="modal-backdrop" onClick={()=>setFineModal(null)}>
          <div className="modal-box" onClick={e=>e.stopPropagation()}>
            <div className="modal-head"><div className="modal-title">Add Fine</div><button className="modal-close" onClick={()=>setFineModal(null)}><XMarkIcon /></button></div>
            <div className="modal-body">
              <div style={{fontWeight:600,fontSize:'.86rem',color:'#0f1123',marginBottom:12}}>{fineModal.student?.user?.name} — {monthLabel(fineModal.month)}</div>
              <div className="form-field"><label className="form-label">Fine Reason *</label>
                <select className="form-select" value={fineForm.reason} onChange={e=>{
                  const p = FINE_PRESETS.find(x=>x.reason===e.target.value);
                  setFineForm({...fineForm, reason:e.target.value, amount: p?.amount ? String(p.amount) : fineForm.amount});
                }}>
                  {FINE_PRESETS.map(p=><option key={p.reason} value={p.reason}>{p.reason}{p.amount>0?` — Rs. ${p.amount}`:''}</option>)}
                </select>
              </div>
              {fineForm.reason==='Other' && <div className="form-field"><label className="form-label">Custom Reason</label><input type="text" className="form-input" required value={fineForm.customReason} onChange={e=>setFineForm({...fineForm,customReason:e.target.value})} /></div>}
              <div className="form-field"><label className="form-label">Description (optional)</label><input type="text" className="form-input" value={fineForm.description} onChange={e=>setFineForm({...fineForm,description:e.target.value})} /></div>
              <div className="form-field"><label className="form-label">Fine Amount (Rs.) *</label><input type="number" min={1} className="form-input mono" value={fineForm.amount} onChange={e=>setFineForm({...fineForm,amount:e.target.value})} /></div>
              {/* FIX: Number() on fineModal.totalDue */}
              {fineForm.amount && <div style={{background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:9,padding:'8px 12px',fontSize:'.78rem',color:'#9a3412'}}>New Total: <strong>{fmt((Number(fineModal.totalDue)||0)+Number(fineForm.amount||0))}</strong></div>}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setFineModal(null)}>Cancel</button>
              <button className="btn btn-amber" onClick={handleAddFine} disabled={addingFine}>{addingFine?'Adding…':'Add Fine'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── Edit Modal ── */}
      {editModal && createPortal(
        <div className="modal-backdrop" onClick={()=>setEditModal(null)}>
          <div className="modal-box" onClick={e=>e.stopPropagation()}>
            <div className="modal-head"><div className="modal-title">Edit Fee Record</div><button className="modal-close" onClick={()=>setEditModal(null)}><XMarkIcon /></button></div>
            <div className="modal-body">
              <div style={{fontWeight:600,fontSize:'.86rem',color:'#0f1123',marginBottom:12}}>{editModal.student?.user?.name} — {monthLabel(editModal.month)}</div>
              <div className="form-field"><label className="form-label">Status</label>
                <select className="form-select" value={editForm.status} onChange={e=>setEditForm({...editForm,status:e.target.value})}>
                  <option value="unpaid">Unpaid</option><option value="paid">Paid</option><option value="overdue">Overdue</option><option value="waived">Waived</option>
                </select>
              </div>
              <div className="form-field"><label className="form-label">Remarks</label><input type="text" className="form-input" value={editForm.remarks} onChange={e=>setEditForm({...editForm,remarks:e.target.value})} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setEditModal(null)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleEdit} disabled={editing}>{editing?'Saving…':'Save Changes'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── Challan Preview ── */}
      {challanRec && createPortal(
        <div className="modal-backdrop" onClick={()=>setChallanRec(null)}>
          <div className="modal-box" style={{maxWidth:560}} onClick={e=>e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">Fee Challan — {challanRec.student?.user?.name}</div>
              <div style={{display:'flex',gap:8,flexShrink:0}}>
                <button className="btn btn-ghost btn-sm" onClick={()=>printChallan(challanRec,challanSchool)}><PrinterIcon />Print</button>
                <button className="modal-close" onClick={()=>setChallanRec(null)}><XMarkIcon /></button>
              </div>
            </div>
            <div className="modal-body">
              <div style={{border:'1px solid #e8ebf4',borderRadius:12,overflow:'hidden'}}>
                <div style={{background:'#1a1d2e',color:'white',padding:'14px 18px',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8}}>
                  <div><div style={{fontWeight:700,fontSize:'.96rem'}}>{challanSchool?.name||'School'}</div><div style={{fontSize:'.72rem',opacity:.7}}>{challanSchool?.address}</div></div>
                  <div style={{fontSize:'.78rem',fontWeight:600,background:'rgba(255,255,255,.15)',padding:'5px 12px',borderRadius:6}}>FEE CHALLAN</div>
                </div>
                <div style={{padding:'14px 18px'}}>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:12}}>
                    {[
                      ['Student',    challanRec.student?.user?.name],
                      ['Admission No', challanRec.student?.admissionNo],
                      ['Class',      `${challanRec.class?.name} — Sec ${challanRec.section}`],
                      ['Month',      monthLabel(challanRec.month)],
                      ['Due Date',   fmtDate(challanRec.dueDate)],
                      ['Status',     STATUS_LABELS[challanRec.status]],
                    ].map(([k,v]) => (
                      <div key={k} style={{background:'#f8f9fc',borderRadius:6,padding:'7px 10px'}}>
                        <div style={{fontSize:'.65rem',color:'#9094a8',textTransform:'uppercase',fontWeight:600}}>{k}</div>
                        <div style={{fontWeight:600,fontSize:'.82rem'}}>{v}</div>
                      </div>
                    ))}
                  </div>
                  <table style={{width:'100%',borderCollapse:'collapse',marginBottom:12}}>
                    <thead><tr style={{background:'#f8f9fc',borderBottom:'1px solid #eee'}}>
                      <th style={{padding:'7px 10px',textAlign:'left',fontSize:'.65rem',color:'#9094a8',textTransform:'uppercase'}}>Description</th>
                      <th style={{padding:'7px 10px',textAlign:'right',fontSize:'.65rem',color:'#9094a8',textTransform:'uppercase'}}>Amount</th>
                    </tr></thead>
                    <tbody>
                      <tr><td style={{padding:'7px 10px',fontSize:'.82rem'}}>Tuition Fee — {monthLabel(challanRec.month)}</td><td style={{padding:'7px 10px',textAlign:'right',fontFamily:'monospace',fontWeight:600}}>{fmt(challanRec.amount)}</td></tr>
                      <tr><td style={{padding:'7px 10px',fontSize:'.82rem'}}>Hostel Fee</td><td style={{padding:'7px 10px',textAlign:'right',fontFamily:'monospace',fontWeight:600}}>{fmtHostel(challanRec.hostelFee)}</td></tr>
                      {(challanRec.fines||[]).map(f=><tr key={f.id||f._id}><td style={{padding:'7px 10px',fontSize:'.82rem',color:'#ea580c'}}>{fineLabel(f)}: {f.reason}</td><td style={{padding:'7px 10px',textAlign:'right',fontFamily:'monospace',fontWeight:600,color:'#ea580c'}}>+ {fmt(f.amount)}</td></tr>)}
                      {Number(challanRec.discount)>0 && <tr><td style={{padding:'7px 10px',fontSize:'.82rem',color:'#10c98e'}}>Discount</td><td style={{padding:'7px 10px',textAlign:'right',fontFamily:'monospace',fontWeight:600,color:'#10c98e'}}>− {fmt(challanRec.discount)}</td></tr>}
                    </tbody>
                    {/* FIX: Number() on totalDue */}
                    <tfoot><tr style={{background:'#f0f9ff',borderTop:'2px solid #3b6ef8'}}><td style={{padding:'9px 10px',fontWeight:700}}>Total Due</td><td style={{padding:'9px 10px',textAlign:'right',fontFamily:'monospace',fontWeight:700,fontSize:'.9rem'}}>{fmt(Number(challanRec.totalDue)||0)}</td></tr></tfoot>
                  </table>
                  {challanRec.status==='paid' && <div style={{background:'#ecfdf5',border:'1px solid #a7f3d0',borderRadius:8,padding:'8px 12px',fontSize:'.78rem',color:'#065f46'}}>✓ Paid on {fmtDate(challanRec.paidAt)}</div>}
                </div>
              </div>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── Discount Modal ── */}
      {discModal && createPortal(
        <div className="modal-backdrop" onClick={()=>setDiscModal(null)}>
          <div className="modal-box" onClick={e=>e.stopPropagation()}>
            <div className="modal-head"><div className="modal-title">Add / Edit Discount</div><button className="modal-close" onClick={()=>setDiscModal(null)}><XMarkIcon /></button></div>
            <div className="modal-body">
              <div style={{fontWeight:600,fontSize:'.86rem',color:'#0f1123',marginBottom:12}}>{discModal.student?.user?.name} — {monthLabel(discModal.month)} · Base: {fmt(discModal.amount)}</div>
              <div className="form-field"><label className="form-label">Discount Amount (Rs.)</label><input type="number" min={0} max={Number(discModal.amount)||0} className="form-input mono" value={discForm.discount} onChange={e=>setDiscForm({...discForm,discount:Number(e.target.value)})} /></div>
              <div className="form-field"><label className="form-label">Reason</label><input type="text" className="form-input" required value={discForm.discountReason} onChange={e=>setDiscForm({...discForm,discountReason:e.target.value})} placeholder="e.g. Scholarship, Sibling discount…" /></div>
              {discForm.discount>0 && (
                <div style={{background:'#ecfdf5',border:'1px solid #a7f3d0',borderRadius:9,padding:'9px 12px',fontSize:'.78rem',color:'#065f46'}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:3}}><span>Base Fee</span><span style={{fontFamily:'monospace'}}>{fmt(discModal.amount)}</span></div>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:3,color:'#10c98e'}}><span>Discount</span><span style={{fontFamily:'monospace'}}>− {fmt(discForm.discount)}</span></div>
                  {/* FIX: Number() on all fine amounts in discount preview */}
                  <div style={{display:'flex',justifyContent:'space-between',fontWeight:700,borderTop:'1px solid #a7f3d0',paddingTop:6,marginTop:4}}><span>New Total</span><span style={{fontFamily:'monospace'}}>{fmt((Number(discModal.amount)||0) + sumFines(discModal.fines) - discForm.discount)}</span></div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setDiscModal(null)}>Cancel</button>
              <button className="btn btn-green" onClick={handleAddDiscount} disabled={addingDisc}><CheckCircleIcon />{addingDisc?'Saving…':'Apply Discount'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── Proof View ── */}
      {proofView && createPortal(
        <div className="modal-backdrop" onClick={()=>setProofView(null)}>
          <div className="modal-box" onClick={e=>e.stopPropagation()}>
            <div className="modal-head"><div className="modal-title">Payment Proof — {proofView.student?.user?.name}</div><button className="modal-close" onClick={()=>setProofView(null)}><XMarkIcon /></button></div>
            <div className="modal-body">
              <div style={{marginBottom:12}}>
                {/* FIX: Number() on totalDue */}
                <div style={{fontWeight:600}}>{monthLabel(proofView.month)} · {fmt(Number(proofView.totalDue)||0)}</div>
                {proofView.paymentProofNote && <div style={{fontSize:'.78rem',color:'#6b7194',marginTop:4}}>{proofView.paymentProofNote}</div>}
                <div style={{fontSize:'.72rem',color:'#9094a8',marginTop:2}}>Submitted: {fmtDate(proofView.proofSubmittedAt||proofView.proof_submitted_at)}</div>
              </div>
              <img src={proofView.paymentProofUrl||proofView.payment_proof_url} alt="Payment proof" className="proof-img"
                onError={e=>{e.target.replaceWith(Object.assign(document.createElement('a'),{href:proofView.paymentProofUrl||proofView.payment_proof_url,target:'_blank',textContent:'Open proof link →',style:'color:#3b6ef8'}));}} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setProofView(null)}>Close</button>
              <button className="btn btn-green" onClick={()=>{openPayModal(proofView);setProofView(null);}}><CheckCircleIcon />Mark as Paid</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── Regenerate Bill Modal ── */}
      {regenModal && createPortal(
        <div className="modal-backdrop" onClick={()=>setRegenModal(null)}>
          <div className="modal-box" onClick={e=>e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">↺ Regenerate Fee Bill</div>
              <button className="modal-close" onClick={()=>setRegenModal(null)}><XMarkIcon /></button>
            </div>
            <div className="modal-body">
              <div style={{background:'#f0fdfa',border:'1px solid #99f6e4',borderRadius:10,padding:'10px 14px',fontSize:'.76rem',color:'#0f766e',marginBottom:14}}>
                The waived bill will be <strong>deleted</strong> and a fresh unpaid bill created for the same student and month.
              </div>
              <div style={{background:'#f8f9fc',borderRadius:10,padding:'10px 14px',marginBottom:14,fontSize:'.82rem'}}>
                <strong>{regenModal.student?.user?.name}</strong>
                <span style={{marginLeft:8,fontFamily:"'JetBrains Mono',monospace",color:'#6b7194'}}>{monthLabel(regenModal.month)}</span>
                <span className="status-badge waived" style={{marginLeft:8}}>Waived</span>
              </div>
              <div className="form-grid">
                <div className="form-field">
                  <label className="form-label">Visible From Day</label>
                  <input type="number" min="1" max="31" className="form-input"
                    value={regenForm.visibleDay}
                    onChange={e=>setRegenForm(p=>({...p,visibleDay:e.target.value}))} />
                </div>
                <div className="form-field">
                  <label className="form-label">Due By Day</label>
                  <input type="number" min="1" max="31" className="form-input"
                    value={regenForm.dueDay}
                    onChange={e=>setRegenForm(p=>({...p,dueDay:e.target.value}))} />
                </div>
                <div className="form-field" style={{gridColumn:'1/-1'}}>
                  {/* FIX: Number() on regenModal.amount */}
                  <label className="form-label">Override Amount (blank = use class fee of {fmt(Number(regenModal.amount)||0)})</label>
                  <input type="number" className="form-input"
                    value={regenForm.amount}
                    onChange={e=>setRegenForm(p=>({...p,amount:e.target.value}))}
                    placeholder={`Default: ${fmt(Number(regenModal.amount)||0)}`} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setRegenModal(null)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleRegen} disabled={regening}>
                {regening?'Regenerating…':'↺ Regenerate Bill'}
              </button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
};

export default Fees;